/**
 * Admin ChatPhrasesPage
 *
 * Manages the predefined chat phrases used by the mobile app. The guest/host
 * chat has no free-text input - users can only send from this curated,
 * digit-free list - so contact details can't be exchanged off-platform.
 * Stored in the shared `quickReplies` collection with a `category` field.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useAuth } from '@/contexts';
import {
  fetchAllQuickReplies,
  createQuickReply,
  updateQuickReply,
  deleteQuickReply,
  seedDefaultChatPhrases,
  CHAT_PHRASE_CATEGORIES,
  type QuickReply,
  type QuickReplyVisibility,
} from '@/services/quickReplies';

interface FormState {
  text: string;
  category: string;
  visibleTo: QuickReplyVisibility;
  order: number;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  text: '',
  category: CHAT_PHRASE_CATEGORIES[0],
  visibleTo: 'all',
  order: 100,
  active: true,
};

const UNCATEGORIZED = 'Uncategorized';

/** Reject anything with a digit so numbers never enter the phrase list. */
const containsDigit = (s: string) => /\d/.test(s);

export const ChatPhrasesPage: React.FC = () => {
  const { user } = useAuth();
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const load = async () => {
    setIsLoading(true);
    try {
      setReplies(await fetchAllQuickReplies());
    } catch (err) {
      console.error('Failed to load chat phrases:', err);
      toast.error('Failed to load chat phrases');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, QuickReply[]>();
    for (const r of replies) {
      const key = r.category || UNCATEGORIZED;
      (map.get(key) ?? map.set(key, []).get(key)!).push(r);
    }
    // Order categories by the canonical list, then any extras.
    const orderedKeys = [
      ...CHAT_PHRASE_CATEGORIES.filter((c) => map.has(c)),
      ...[...map.keys()].filter(
        (k) => !CHAT_PHRASE_CATEGORIES.includes(k as never)
      ),
    ];
    return orderedKeys.map((k) => ({ category: k, items: map.get(k)! }));
  }, [replies]);

  const handleEdit = (r: QuickReply) => {
    setEditingId(r.id);
    setForm({
      text: r.text,
      category: r.category || CHAT_PHRASE_CATEGORIES[0],
      visibleTo: r.visibleTo,
      order: r.order,
      active: r.active,
    });
    // Scroll the form into view - AdminLayout scrolls an inner container, not
    // the window, so window.scrollTo would be a no-op and the edit would look
    // like nothing happened.
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = form.text.trim();
    if (!text) {
      toast.error('Please enter the phrase');
      return;
    }
    if (containsDigit(text)) {
      toast.error('Phrases cannot contain numbers');
      return;
    }
    setIsSaving(true);
    try {
      if (editingId) {
        await updateQuickReply(editingId, { ...form, text });
        toast.success('Phrase updated');
      } else {
        await createQuickReply({ ...form, text }, user?.uid || 'unknown');
        toast.success('Phrase added');
      }
      await load();
      handleCancel();
    } catch (err) {
      console.error('Failed to save phrase:', err);
      toast.error('Failed to save phrase');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (r: QuickReply) => {
    try {
      await updateQuickReply(r.id, { active: !r.active });
      await load();
    } catch (err) {
      console.error('Failed to toggle phrase:', err);
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (r: QuickReply) => {
    if (!confirm(`Delete "${r.text}"?`)) return;
    try {
      await deleteQuickReply(r.id);
      toast.success('Phrase deleted');
      await load();
    } catch (err) {
      console.error('Failed to delete phrase:', err);
      toast.error('Failed to delete');
    }
  };

  const handleSeed = async () => {
    if (
      !confirm(
        'Add the default phrase set? This appends the built-in phrases; ' +
          'it does not remove any you already have.'
      )
    )
      return;
    setIsSeeding(true);
    try {
      const n = await seedDefaultChatPhrases(user?.uid || 'unknown');
      toast.success(`Added ${n} default phrases`);
      await load();
    } catch (err) {
      console.error('Failed to seed phrases:', err);
      toast.error('Failed to add default phrases');
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Chat phrases</h1>
            <p className="text-sm text-gray-500">
              The mobile chat only lets guests and hosts send from this list.
              There is no free-text input, so keep phrases digit-free and phone
              numbers can never be shared.
            </p>
          </div>
          <button
            onClick={handleSeed}
            disabled={isSeeding}
            className="shrink-0 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {isSeeding ? 'Adding…' : 'Load default phrases'}
          </button>
        </div>

        {/* Form */}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className={`bg-white rounded-xl border p-5 space-y-4 scroll-mt-4 ${
            editingId ? 'border-primary-400 ring-1 ring-primary-200' : 'border-gray-200'
          }`}
        >
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Editing phrase' : 'Add a new phrase'}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phrase
            </label>
            <input
              type="text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="e.g. Can we add one more mattress?"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              {form.text.length}/200 characters · no numbers
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {CHAT_PHRASE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Show to
              </label>
              <select
                value={form.visibleTo}
                onChange={(e) =>
                  setForm({ ...form, visibleTo: e.target.value as QuickReplyVisibility })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Everyone</option>
                <option value="host">Hosts only</option>
                <option value="guest">Guests only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>
              <input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Active (shown to users)</span>
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : editingId ? 'Save changes' : 'Add phrase'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Grouped list */}
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : replies.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            No phrases yet. Add one above, or click{' '}
            <span className="font-medium">Load default phrases</span>.
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ category, items }) => (
              <div
                key={category}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {category} · {items.length}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <td className="px-4 py-3 text-gray-900">{r.text}</td>
                        <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">
                          {r.visibleTo === 'all' ? 'Everyone' : r.visibleTo + 's'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(r)}
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              r.active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {r.active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => handleEdit(r)}
                            className="text-primary-600 hover:text-primary-700 font-medium mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(r)}
                            className="text-red-600 hover:text-red-700 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChatPhrasesPage;
